import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Wallet,
  Calendar,
  ArrowUpCircle,
  ArrowDownCircle,
  RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/authService';

const CashFlowDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [period, setPeriod] = useState('current_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadDashboard();
  }, [period, startDate, endDate]);

  const loadDashboard = async () => {
    try {
      setLoading(true);

      let params = {};

      if (period === 'custom' && startDate && endDate) {
        params = { start_date: startDate, end_date: endDate };
      } else {
        const dates = getPeriodDates(period);
        params = { start_date: dates.start, end_date: dates.end };
      }

      const response = await api.get('/api/cash-flow/dashboard', { params });
      setDashboard(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      toast.error('Erro ao carregar dados do fluxo de caixa');
    } finally {
      setLoading(false);
    }
  };

  const getPeriodDates = (period) => {
    const today = new Date();
    let start, end;

    switch (period) {
      case 'today':
        start = end = today.toISOString().split('T')[0];
        break;
      case 'current_week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        start = weekStart.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'current_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
        break;
      case 'current_year':
        start = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        end = new Date(today.getFullYear(), 11, 31).toISOString().split('T')[0];
        break;
      default:
        start = end = today.toISOString().split('T')[0];
    }

    return { start, end };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const summary = dashboard?.summary || {};
  const incomeByCategory = dashboard?.income_by_category || [];
  const expenseByCategory = dashboard?.expense_by_category || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Fluxo de Caixa
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Visão geral das suas finanças
          </p>
        </div>

        {/* Filtro de Período */}
        <div className="flex gap-2 flex-wrap">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value="today">Hoje</option>
            <option value="current_week">Semana Atual</option>
            <option value="current_month">Mês Atual</option>
            <option value="current_year">Ano Atual</option>
            <option value="custom">Personalizado</option>
          </select>

          {period === 'custom' && (
            <>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
              />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
              />
            </>
          )}

          <button
            onClick={loadDashboard}
            className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Saldo Atual */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <Wallet className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-80">Saldo em Contas</span>
          </div>
          <p className="text-3xl font-bold">{formatCurrency(summary.current_balance)}</p>
          <p className="text-sm opacity-80 mt-2">
            Projetado: {formatCurrency(summary.projected_balance)}
          </p>
        </div>

        {/* Total Receitas */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <TrendingUp className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-80">Receitas</span>
          </div>
          <p className="text-3xl font-bold">{formatCurrency(summary.total_income)}</p>
          <p className="text-sm opacity-80 mt-2">
            Pendente: {formatCurrency(summary.pending_income)}
          </p>
        </div>

        {/* Total Despesas */}
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <TrendingDown className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-80">Despesas</span>
          </div>
          <p className="text-3xl font-bold">{formatCurrency(summary.total_expense)}</p>
          <p className="text-sm opacity-80 mt-2">
            Pendente: {formatCurrency(summary.pending_expense)}
          </p>
        </div>

        {/* Saldo Líquido */}
        <div className={`rounded-xl p-6 text-white shadow-lg ${
          summary.net_balance >= 0
            ? 'bg-gradient-to-br from-purple-500 to-purple-600'
            : 'bg-gradient-to-br from-orange-500 to-orange-600'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <DollarSign className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-80">Saldo Líquido</span>
          </div>
          <p className="text-3xl font-bold">{formatCurrency(summary.net_balance)}</p>
          <p className="text-sm opacity-80 mt-2">
            {summary.net_balance >= 0 ? 'Positivo' : 'Negativo'}
          </p>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Receitas por Categoria */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <ArrowUpCircle className="w-5 h-5 text-green-500" />
            Receitas por Categoria
          </h3>
          <div className="space-y-3">
            {incomeByCategory.length > 0 ? (
              incomeByCategory.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.color || '#10B981' }}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {item.name || 'Sem categoria'}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(item.total)}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">
                Nenhuma receita no período
              </p>
            )}
          </div>
        </div>

        {/* Despesas por Categoria */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <ArrowDownCircle className="w-5 h-5 text-red-500" />
            Despesas por Categoria
          </h3>
          <div className="space-y-3">
            {expenseByCategory.length > 0 ? (
              expenseByCategory.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.color || '#EF4444' }}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {item.name || 'Sem categoria'}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(item.total)}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">
                Nenhuma despesa no período
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Ações Rápidas */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Ações Rápidas
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <a
            href="/cash-flow/transactions?type=income"
            className="p-4 border-2 border-green-200 dark:border-green-800 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors text-center"
          >
            <ArrowUpCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900 dark:text-white">Nova Receita</p>
          </a>
          <a
            href="/cash-flow/transactions?type=expense"
            className="p-4 border-2 border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-center"
          >
            <ArrowDownCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900 dark:text-white">Nova Despesa</p>
          </a>
          <a
            href="/cash-flow/transfers"
            className="p-4 border-2 border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-center"
          >
            <RefreshCw className="w-6 h-6 text-blue-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900 dark:text-white">Transferência</p>
          </a>
          <a
            href="/cash-flow/reports"
            className="p-4 border-2 border-purple-200 dark:border-purple-800 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors text-center"
          >
            <Calendar className="w-6 h-6 text-purple-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900 dark:text-white">Relatórios</p>
          </a>
        </div>
      </div>
    </div>
  );
};

export default CashFlowDashboard;